import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  Button,
  Typography, 
  TextField, 
  Paper, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  InputAdornment,
  useTheme,
  CircularProgress,
  Alert,
  TablePagination
} from '@mui/material';
import { 
  Search as SearchIcon,
  ViewColumn as ViewColumnIcon
} from '@mui/icons-material';
import { machinesService } from '../services/api';
import { ColumnSelector, ColumnConfig } from '../components/ColumnSelector';

const SESSION_COLUMNS: ColumnConfig[] = [
  { key: 'date', label: 'Date', visible: true },
  { key: 'start_time', label: 'Start Time', visible: true },
  { key: 'duration', label: 'Duration', visible: true },
  { key: 'energy', label: 'Energy', visible: true },
  { key: 'avg_power', label: 'Avg Power', visible: true },
  { key: 'max_power', label: 'Max Power', visible: true },
  { key: 'start_soc', label: 'Battery at start', visible: true },
  { key: 'end_soc', label: 'Battery at end', visible: true },
];

interface ChargingSessionData {
  id: string;
  date: string;
  start_time: string;
  duration: number | null;
  energy: number | null;
  avg_power: number | null;
  max_power: number | null;
  start_soc: number | null;
  end_soc: number | null;
  unit_name: string;
}

interface MachineChargingSessionApiData {
  avg_power: number | null;
  date: string;
  duration: number | null;
  end_soc: number | null;
  end_time: string | null;
  energy: number | null;
  max_power: number | null;
  org_id: string;
  start_soc: number | null;
  start_time: string;
  unit_id: string;
  unit_name: string;
}

const MachineChargingSessions: React.FC = () => {
  const theme = useTheme();
  const { orgId, unitId } = useParams<{ orgId: string; unitId: string }>();
  const [searchTerm, setSearchTerm] = useState('');
  const [chargingSessions, setChargingSessions] = useState<ChargingSessionData[]>([]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [columns, setColumns] = useState<ColumnConfig[]>(SESSION_COLUMNS);
  const [columnSelectorOpen, setColumnSelectorOpen] = useState(false);
  const columnButtonRef = useRef<HTMLButtonElement>(null);
  const isInitialLoadRef = useRef(true);
  const visibleColumns = columns.filter((column) => column.visible);
  
  // Date range state - default to last week (including today)
  const [dateRange, setDateRange] = useState(() => ({
    start: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000),
    end: new Date()
  }));

  useEffect(() => {
    let isCancelled = false;

    const fetchChargingSessions = async () => {
      if (!orgId || !unitId) {
        if (!isCancelled) {
          setError('Missing organization ID or unit ID');
          setLoading(false);
        }
        return;
      }

      try {
        if (isInitialLoadRef.current) {
          setLoading(true);
        } else {
          setDataLoading(true);
        }
        setError(null);

        const fromDate = formatDateForInput(dateRange.start);
        const toDate = formatDateForInput(dateRange.end);
        const response = await machinesService.getMachineChargingSessions(orgId, unitId, fromDate, toDate);

        if (!response.success) {
          throw new Error('Failed to fetch charging sessions');
        }

        const sessions = (response.data?.data || []) as MachineChargingSessionApiData[];
        const mappedSessions: ChargingSessionData[] = sessions.map((session, index) => ({
          id: `${session.unit_id}-${session.start_time}-${index}`,
          date: session.date,
          start_time: session.start_time,
          duration: session.duration,
          energy: session.energy,
          avg_power: session.avg_power,
          max_power: session.max_power,
          start_soc: session.start_soc,
          end_soc: session.end_soc,
          unit_name: session.unit_name,
        }));

        if (!isCancelled) {
          setChargingSessions(mappedSessions);
        }
      } catch (err) {
        console.error('Error fetching charging sessions:', err);
        if (!isCancelled) {
          setError(err instanceof Error ? err.message : 'Failed to fetch charging sessions');
        }
      } finally {
        if (!isCancelled) {
          isInitialLoadRef.current = false;
          setLoading(false);
          setDataLoading(false);
        }
      }
    };

    fetchChargingSessions();

    return () => {
      isCancelled = true;
    };
  }, [orgId, unitId, dateRange.start, dateRange.end]);

  const searchText = (value: unknown) => (typeof value === 'string' ? value : '').toLowerCase();
  const sortText = (value: unknown) => (typeof value === 'string' ? value : '');

  const filteredData = (chargingSessions || [])
    .filter(session =>
      searchText(session.unit_name).includes(searchTerm.toLowerCase()) ||
      searchText(session.date).includes(searchTerm.toLowerCase()) ||
      searchText(session.start_time).includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      
      if (dateB !== dateA) {
        return dateB - dateA;
      }
      
      const timeA = sortText(a.start_time);
      const timeB = sortText(b.start_time);
      
      if (timeB > timeA) return 1;
      if (timeB < timeA) return -1;
      return 0;
    });

  const toFiniteNumber = (value: unknown): number | null => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const displayText = (value: unknown) => {
    if (typeof value === 'string') return value || 'N/A';
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return 'N/A';
  };

  const formatEnergy = (energy: unknown) => {
    const value = toFiniteNumber(energy);
    return value === null ? 'N/A' : `${value.toFixed(1)} kWh`;
  };

  const formatPower = (power: unknown) => {
    const value = toFiniteNumber(power);
    return value === null ? 'N/A' : `${value.toFixed(1)} kW`;
  };

  const formatSoC = (soc: unknown) => {
    const value = toFiniteNumber(soc);
    return value === null ? 'N/A' : `${Math.round(value)} %`;
  };

  const formatDuration = (duration: unknown) => {
    const value = toFiniteNumber(duration);
    if (value === null) return 'N/A';
    const roundedDuration = Math.round(value);
    const hours = Math.floor(roundedDuration / 60);
    const minutes = roundedDuration % 60;
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const formatTime = (time: unknown, date: unknown) => {
    if (typeof time !== 'string' || !time) return 'N/A';

    try {
      let utcDate: Date;

      if (time.includes('T') || time.includes('Z') || time.includes('+')) {
        utcDate = new Date(time);
      } else if (typeof date === 'string' && date) {
        utcDate = new Date(`${date}T${time}Z`);
      } else {
        return time;
      }

      if (isNaN(utcDate.getTime())) {
        return time;
      }

      return utcDate.toLocaleTimeString('en-GB', {
        timeZone: 'Europe/Oslo',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
      });
    } catch (formatError) {
      console.error('Error formatting time:', formatError);
      return time;
    }
  };

  // Format date for input value
  const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getCellText = (session: ChargingSessionData, key: string) => {
    try {
      switch (key) {
        case 'date':
          return displayText(session.date);
        case 'start_time':
          return formatTime(session.start_time, session.date);
        case 'duration':
          return formatDuration(session.duration);
        case 'energy':
          return formatEnergy(session.energy);
        case 'avg_power':
          return formatPower(session.avg_power);
        case 'max_power':
          return formatPower(session.max_power);
        case 'start_soc':
          return formatSoC(session.start_soc);
        case 'end_soc':
          return formatSoC(session.end_soc);
        default:
          return '';
      }
    } catch (error) {
      console.error('Error rendering charging session cell:', error);
      return 'N/A';
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1, backgroundColor: '#FAFAFA', p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ flexGrow: 1, backgroundColor: '#FAFAFA', p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#FAFAFA', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Charging Sessions
        </Typography>
      </Box>
      <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 4 }}>
        This is the Charging Sessions page. Here you can view and manage charging session data.
      </Typography>

      {/* Date Range and Search Section */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', flexWrap: 'wrap', mb: 3, gap: 2 }}>
        {/* Date Range */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <input
              type="date"
              value={formatDateForInput(dateRange.start)}
              onChange={(e) => {
                if (e.target.value) {
                  setDateRange(prev => ({ ...prev, start: new Date(e.target.value) }));
                }
              }}
              style={{
                width: '150px',
                height: '40px',
                padding: '8px 12px',
                fontSize: '16px',
                fontFamily: 'Montserrat',
                border: '1px solid rgba(0,0,0,0.23)',
                borderRadius: '4px',
                backgroundColor: 'transparent',
                color: 'rgba(0,0,0,0.87)',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#1976d2';
                e.target.style.borderWidth = '2px';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(0,0,0,0.23)';
                e.target.style.borderWidth = '1px';
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                top: '-8px',
                left: '12px',
                backgroundColor: '#FAFAFA',
                padding: '0 4px',
                fontSize: '12px',
                color: 'rgba(0,0,0,0.6)',
                fontFamily: 'Montserrat',
              }}
            >
              Start Date
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', fontFamily: 'Montserrat' }}>
            –
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <input
              type="date"
              value={formatDateForInput(dateRange.end)}
              onChange={(e) => {
                if (e.target.value) {
                  setDateRange(prev => ({ ...prev, end: new Date(e.target.value) }));
                }
              }}
              style={{
                width: '150px',
                height: '40px',
                padding: '8px 12px',
                fontSize: '16px',
                fontFamily: 'Montserrat',
                border: '1px solid rgba(0,0,0,0.23)',
                borderRadius: '4px',
                backgroundColor: 'transparent',
                color: 'rgba(0,0,0,0.87)',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#1976d2';
                e.target.style.borderWidth = '2px';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(0,0,0,0.23)';
                e.target.style.borderWidth = '1px';
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                top: '-8px',
                left: '12px',
                backgroundColor: '#FAFAFA',
                padding: '0 4px',
                fontSize: '12px',
                color: 'rgba(0,0,0,0.6)',
                fontFamily: 'Montserrat',
              }}
            >
              End Date
            </Typography>
          </Box>
        </Box>

        {/* Search Field */}
        <TextField
          placeholder="Search by unit name, date, or start time..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0);
          }}
          size="small"
          sx={{ 
            width: 400,
            '& .MuiOutlinedInput-root': {
              height: '40px',
              borderRadius: '4px',
            },
            '& .MuiInputBase-input': {
              height: '40px',
              padding: '0 14px',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
          }}
        />

        <Button
          ref={columnButtonRef}
          variant="text"
          startIcon={<ViewColumnIcon />}
          onClick={() => setColumnSelectorOpen((open) => !open)}
          sx={{ color: '#337A5C' }}
        >
          Columns
        </Button>

        <ColumnSelector
          columns={columns}
          onColumnsChange={setColumns}
          open={columnSelectorOpen}
          onClose={() => setColumnSelectorOpen(false)}
          anchorEl={columnButtonRef.current}
        />
      </Box>

      {/* Charging Sessions Table */}
      <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', position: 'relative' }}>
        {dataLoading && (
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'rgba(255, 255, 255, 0.8)', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            zIndex: 1 
          }}>
            <CircularProgress />
          </Box>
        )}
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              {visibleColumns.map((column) => (
                <TableCell key={column.key} sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((session, index) => (
              <TableRow key={`${session.id ?? 'session'}-${index}`} sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                {visibleColumns.map((column) => (
                  <TableCell key={column.key}>
                    <Typography
                      variant="body2"
                      sx={{ color: column.key === 'energy' ? theme.palette.text.primary : theme.palette.text.secondary }}
                    >
                      {getCellText(session, column.key)}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ 
          borderTop: '1px solid rgba(0,0,0,0.12)',
          '& .MuiTablePagination-toolbar': {
            paddingLeft: 0,
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.6)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-selectLabel': {
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.6)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-displayedRows': {
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.87)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-select': {
            fontFamily: 'Roboto',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.87)',
            letterSpacing: '0.3px',
            lineHeight: '19px'
          }
        }}
      />

      {/* No results message */}
      {filteredData.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No charging sessions found matching your search criteria.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MachineChargingSessions;

